import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import { resendVerification } from "@/api/auth";
import { responsiveAlertActionSx } from "@/utils/sx";

interface Props {
  email: string;
}

type Status = "idle" | "sending" | "sent" | "rate-limited" | "error";

export default function UnverifiedBanner({ email }: Props) {
  const [status, setStatus] = useState<Status>("idle");

  useEffect(() => {
    if (status !== "rate-limited") return;
    const t = setTimeout(() => setStatus("idle"), 60_000);
    return () => clearTimeout(t);
  }, [status]);

  const handleResend = async () => {
    if (status === "sending") return;
    setStatus("sending");
    try {
      await resendVerification();
      setStatus("sent");
    } catch (err: unknown) {
      const httpStatus =
        err !== null &&
        typeof err === "object" &&
        "response" in err &&
        err.response !== null &&
        typeof err.response === "object" &&
        "status" in err.response
          ? (err.response as { status: number }).status
          : 0;
      if (httpStatus === 429) {
        setStatus("rate-limited");
      } else {
        setStatus("error");
      }
    }
  };

  const feedbackText =
    status === "sent"
      ? "Sent — check your inbox."
      : status === "rate-limited"
        ? "Please wait a minute before resending."
        : status === "error"
          ? "Couldn't send right now. Try again."
          : null;

  return (
    <Alert
      severity="warning"
      sx={{ mb: 3, ...responsiveAlertActionSx }}
      action={
        <Button
          color="inherit"
          size="small"
          disabled={status === "sending"}
          onClick={handleResend}
        >
          Resend email
        </Button>
      }
    >
      <AlertTitle>Verify your email</AlertTitle>
      {feedbackText ? null : (
        <>
          We sent a verification link to <strong>{email}</strong>. Please check
          your inbox to confirm your address.
        </>
      )}
      <span role="status" aria-live="polite">
        {feedbackText ?? ""}
      </span>
    </Alert>
  );
}
